"use client"
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export const PresentationBuilderVisuals = () => {
    const [activeStep, setActiveStep] = useState(0);

    // Auto-cycle through steps
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveStep((prev) => (prev + 1) % 4);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="w-full max-w-[500px] aspect-[4/3] bg-[#ffffff] rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.06)] border border-gray-100 relative overflow-hidden flex flex-col font-jakarta group ring-1 ring-gray-50/50">

            {/* Window Chrome */}
            <div className="h-10 border-b border-gray-50 bg-[#ffffff] flex items-center px-4 justify-between shrink-0 z-20">
                <div className="flex gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57] border border-[#e0443e]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e] border border-[#d89e24]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#28c840] border border-[#1aab29]/30" />
                </div>
                <div className="flex items-center gap-2">
                    <div className="px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100">
                        <span className="text-[9px] font-semibold text-gray-400 tracking-wide uppercase">AI Presentation Builder</span>
                    </div>
                </div>
                <div className="w-12" />
            </div>

            {/* Main Content Area */}
            <div className="flex-1 relative overflow-hidden bg-[#ffffff] p-6 lg:p-8">
                <AnimatePresence mode="wait">
                    {activeStep === 0 && <Step1_Describe key="step1" />}
                    {activeStep === 1 && <Step2_Outline key="step2" />}
                    {activeStep === 2 && <Step3_Theme key="step3" />}
                    {activeStep === 3 && <Step4_Edit key="step4" />}
                </AnimatePresence>

                {/* Progressive Step Indicator */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-50 flex">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="flex-1 relative h-full">
                            {activeStep === i && (
                                <motion.div
                                    className="absolute inset-0 bg-[#2563eb]"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 5, ease: "linear" }}
                                />
                            )}
                            {activeStep > i && <div className="absolute inset-0 bg-[#2563eb] opacity-30" />}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- Step Components ---

// Step 1: Describe your presentation
const Step1_Describe = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full items-center justify-center gap-6"
    >
        <div className="w-full max-w-[320px] bg-white rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.08)] border border-gray-100 p-1">
            <div className="bg-gray-50/50 rounded-lg p-3 border border-gray-50">
                <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    </div>
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">AI Prompt</span>
                </div>
                <div className="text-sm text-gray-800 font-medium leading-relaxed">
                    Create a presentation about <span className="text-blue-600">Quarterly Growth</span> strategy for Q3...
                    <motion.span
                        animate={{ opacity: [1, 0] }}
                        transition={{ repeat: Infinity, duration: 0.8 }}
                        className="inline-block w-0.5 h-4 bg-blue-600 align-middle ml-0.5"
                    />
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="px-3 py-2 flex justify-end"
            >
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-200">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                </div>
            </motion.div>
        </div>
    </motion.div>
);

// Step 2: AI creates the outline
const Step2_Outline = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full bg-[#ffffff]"
    >
        <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                <div className="text-xs font-bold text-gray-900">Generating Outline...</div>
            </div>
            <div className="text-[9px] px-2 py-1 bg-blue-50 text-blue-700 rounded-full font-bold border border-blue-100 uppercase tracking-wider">AI Magic</div>
        </div>

        <div className="space-y-3 relative">
            <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gray-100 rounded-full" />

            {[
                { title: "Executive Summary", highlight: false },
                { title: "Q2 Performance Review", highlight: false },
                { title: "Growth Opportunities", highlight: true },
                { title: "Strategic Initiatives", highlight: false }
            ].map((item, i) => (
                <motion.div
                    key={i}
                    initial={{ x: -10, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.2 }}
                    className={`relative ml-1 p-3 rounded-xl border flex items-center gap-3 bg-[#ffffff] group ${item.highlight ? 'border-blue-200 shadow-sm ring-1 ring-blue-50' : 'border-gray-50 hover:border-gray-100'}`}
                >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center bg-white z-10 ${item.highlight ? 'border-blue-500' : 'border-gray-200'}`}>
                        {item.highlight && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                    </div>

                    <div className="flex-1">
                        <div className={`text-xs font-bold ${item.highlight ? 'text-blue-700' : 'text-gray-700'}`}>{item.title}</div>
                    </div>

                    {item.highlight && (
                        <div className="px-2 py-0.5 bg-blue-50 rounded text-[9px] text-blue-600 font-bold">Editing</div>
                    )}
                </motion.div>
            ))}
        </div>
    </motion.div>
);

// Step 3: Choose a theme
const Step3_Theme = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full gap-5 bg-[#ffffff]"
    >
        <div className="flex justify-between items-end border-b border-gray-50 pb-2">
            <div className="space-y-0.5">
                <div className="text-xs font-bold text-gray-900">Select Design Style</div>
                <div className="text-[9px] text-gray-400">Choose from 12 premium templates</div>
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-full border border-gray-100">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" />
                <span className="text-[9px] font-bold text-gray-600">Brand Match On</span>
            </div>
        </div>

        <div className="grid grid-cols-2 gap-4 h-full px-1">
            {/* Theme Card 1 (Unselected) */}
            <div className="group relative rounded-xl border border-gray-100 bg-white p-2 flex flex-col gap-2 hover:border-gray-200 transition-colors">
                <div className="w-full aspect-[4/3] bg-gray-50 rounded border border-gray-50 relative overflow-hidden">
                    <div className="absolute top-2 left-2 w-8 h-1 bg-gray-200 rounded-full" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-30">
                        <div className="w-10 h-10 rounded-full border-2 border-gray-200" />
                    </div>
                </div>
                <div className="flex justify-between items-center px-1">
                    <div className="h-1.5 w-12 bg-gray-100 rounded-full" />
                </div>
            </div>

            {/* Theme Card 2 - Selected (Modern Light) */}
            <motion.div
                initial={{ y: 5, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="relative rounded-xl border-2 border-blue-500 bg-[#ffffff] p-2.5 shadow-[0_8px_30px_rgb(0,0,0,0.08)] flex flex-col gap-2 ring-2 ring-blue-500/10 scale-105"
            >
                {/* Checkmark Badge */}
                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white shadow-sm z-20 ring-2 ring-white">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                </div>

                {/* Theme Preview */}
                <div className="w-full aspect-[4/3] bg-gradient-to-br from-white to-gray-50 rounded-lg relative overflow-hidden border border-gray-100 group">
                    {/* Slide Content */}
                    <div className="absolute top-3 left-3 right-3 bottom-3 flex flex-col">
                        <div className="w-6 h-6 rounded bg-blue-50 flex items-center justify-center mb-auto text-blue-600">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        </div>
                        <div className="space-y-1.5">
                            <div className="text-[10px] font-bold text-gray-900 leading-tight">Minimalist<br />Light</div>
                            <div className="h-0.5 w-8 bg-blue-500 rounded-full" />
                        </div>
                    </div>
                    {/* Decoration */}
                    <div className="absolute -right-4 -bottom-4 w-16 h-16 bg-blue-500/5 rounded-full blur-xl" />
                </div>

                {/* Swatches */}
                <div className="flex justify-between items-center px-1">
                    <span className="text-[9px] font-bold text-gray-900">Modern Clean</span>
                    <div className="flex -space-x-1">
                        <div className="w-3 h-3 rounded-full bg-white border border-gray-200 relative z-30" />
                        <div className="w-3 h-3 rounded-full bg-gray-100 border border-gray-200 relative z-20" />
                        <div className="w-3 h-3 rounded-full bg-blue-500 border border-white relative z-10" />
                    </div>
                </div>
            </motion.div>

            {/* Theme Card 3 (Unselected) */}
            <div className="hidden sm:flex group relative rounded-xl border border-gray-100 bg-white p-2 flex-col gap-2 hover:border-gray-200 transition-colors opacity-60">
                <div className="w-full aspect-[4/3] bg-gray-50 rounded border border-gray-50 relative overflow-hidden">
                    <div className="absolute bottom-0 inset-x-0 h-1/2 bg-gray-200/50 -skew-y-6 origin-bottom-right" />
                </div>
                <div className="flex justify-between items-center px-1">
                    <div className="h-1.5 w-16 bg-gray-100 rounded-full" />
                </div>
            </div>
        </div>
    </motion.div>
);

// Step 4: Edit slides with AI (Enhanced UI)
const Step4_Edit = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full relative bg-blue-50/30"
    >
        {/* Editor Chrome/Toolbar */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-blue-100/50 bg-white/50 backdrop-blur-sm">
            <div className="flex gap-2">
                <div className="flex items-center gap-1 bg-white border border-blue-100 rounded-md px-1.5 py-1 text-[9px] font-bold text-blue-600 shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                    AI Copilot
                </div>
                <div className="w-px h-4 bg-blue-200/50 my-auto" />
                <div className="p-1 rounded hover:bg-blue-50 text-blue-400 cursor-pointer"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg></div>
                <div className="p-1 rounded hover:bg-blue-50 text-blue-400 cursor-pointer"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg></div>
            </div>
            <div className="flex items-center gap-1.5">
                <span className="text-[8px] font-semibold text-blue-300 uppercase tracking-wider">Auto-saving</span>
                <div className="w-1.5 h-1.5 rounded-full border border-blue-300 border-t-blue-600 animate-spin" />
            </div>
        </div>

        {/* Workspace */}
        <div className="flex-1 flex gap-4 p-3 relative overflow-hidden">
            {/* Sidebar Thumbnails */}
            <div className="w-10 flex flex-col gap-2 shrink-0">
                {[1, 2, 3].map(i => (
                    <motion.div
                        key={i}
                        initial={{ x: -10, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.1 * i }}
                        className={`aspect-[4/3] rounded-lg border transition-all duration-300 ${i === 2 ? 'border-blue-500 shadow-md ring-2 ring-blue-500/20 bg-white' : 'border-blue-100 bg-white/60 hover:border-blue-300'}`}
                    >
                        <div className={`w-full h-full p-1 flex flex-col gap-0.5 ${i === 2 ? 'opacity-100' : 'opacity-40'}`}>
                            <div className="w-1/2 h-0.5 bg-blue-300 rounded-full" />
                            <div className="w-full h-2 bg-blue-100/50 rounded" />
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Active Slide Stage */}
            <div className="flex-1 relative perspective-1000">
                <motion.div
                    initial={{ rotateX: 5, opacity: 0, y: 10 }}
                    animate={{ rotateX: 0, opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full h-full bg-white rounded-xl shadow-xl border border-blue-100/80 relative overflow-hidden flex flex-col"
                >
                    {/* Slide Content */}
                    <div className="flex-1 p-5 relative">
                        {/* Slide Title */}
                        <motion.div
                            initial={{ y: 5, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="text-lg font-bold text-slate-900 mb-1"
                        >
                            Q3 Growth Strategy
                        </motion.div>
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: "40%" }}
                            transition={{ delay: 0.4, duration: 0.8 }}
                            className="h-1 bg-blue-500 rounded-full mb-6"
                        />

                        {/* Chart Area */}
                        <div className="grid grid-cols-2 gap-4 h-32">
                            {/* Left: Bullet Points */}
                            <div className="space-y-3">
                                {[
                                    { text: "Expand Market Share", w: "90%" },
                                    { text: "Optimize Ad Spend", w: "75%" },
                                    { text: "Launch New Feature", w: "85%" }
                                ].map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ x: -5, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: 0.5 + (idx * 0.1) }}
                                    >
                                        <div className="text-[10px] font-bold text-slate-600 mb-1">{item.text}</div>
                                        <div className="h-1.5 w-full bg-blue-50 rounded-full overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: item.w }}
                                                transition={{ delay: 0.8 + (idx * 0.1), duration: 1 }}
                                                className="h-full bg-blue-500 rounded-full"
                                            />
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Right: Bar Chart */}
                            <div className="flex items-end justify-between gap-1.5 h-full pb-2 px-2 bg-blue-50/20 rounded-lg border border-blue-50">
                                <motion.div initial={{ height: 0 }} animate={{ height: "40%" }} transition={{ delay: 0.7 }} className="w-full bg-blue-200 rounded-t-sm" />
                                <motion.div initial={{ height: 0 }} animate={{ height: "65%" }} transition={{ delay: 0.8 }} className="w-full bg-blue-300 rounded-t-sm" />
                                <motion.div initial={{ height: 0 }} animate={{ height: "90%" }} transition={{ delay: 0.9 }} className="w-full bg-blue-600 rounded-t-sm shadow-lg shadow-blue-500/20" />
                                <motion.div initial={{ height: 0 }} animate={{ height: "55%" }} transition={{ delay: 1.0 }} className="w-full bg-blue-300 rounded-t-sm" />
                            </div>
                        </div>
                    </div>

                    {/* AI Cursor Agent Overlay */}
                    <motion.div
                        className="absolute bottom-6 right-6 z-20"
                        initial={{ opacity: 0, x: 20, y: 20 }}
                        animate={{ opacity: 1, x: 0, y: 0 }}
                        transition={{ delay: 1.2 }}
                    >
                        <div className="relative">
                            <svg className="w-5 h-5 text-black drop-shadow-xl absolute -top-3 -left-3" fill="currentColor" viewBox="0 0 24 24"><path d="M5.63605 0.500003L22.128 6.784L13.344 10.992L9.20805 19.8L2.83205 3.328L5.63605 0.500003Z" /></svg>
                            <div className="flex items-center gap-2 bg-gray-900/90 text-white px-3 py-1.5 rounded-full shadow-2xl border border-gray-700 backdrop-blur-md">
                                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse" />
                                <span className="text-[9px] font-bold tracking-wide">Refining Strategy...</span>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    </motion.div>
);
